import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { Truck, MapPin, ChevronDown, X, ShieldCheck, Info } from 'lucide-react';
import { useBag } from '../../contexts/BagContext';
import { useRegion } from '../../contexts/RegionContext';
import { useAuth } from '../../contexts/AuthContext';

const BagPage = () => {
  const { items, removeItem, updateQuantity, subtotal, clearBag } = useBag();
  const { formatPrice, region } = useRegion();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showPinInput, setShowPinInput] = useState(false);
  const [pinCode, setPinCode] = useState('');
  const [showQuestionsAbout, setShowQuestionsAbout] = useState(false);

  const shipping = 0; // Free shipping
  const total = subtotal + shipping;

  // Empty bag
  if (items.length === 0) {
    return (
      <div className="store-page bg-white dark:bg-[#f5f5f7] min-h-screen transition-colors duration-400">
        <div className="max-w-[980px] mx-auto px-6 py-16 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="text-[32px] font-bold text-[#1d1d1f] mb-4">Your bag is empty.</h1>
            <p className="text-[17px] text-[#6e6e73] mb-8 max-w-[480px] mx-auto leading-relaxed">
              Looks like you haven't added anything to your bag yet. Browse the store and find the perfect ARK for your space.
            </p>
            <Link to="/shop" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#065DC0] text-white text-[15px] font-semibold hover:bg-[#0077ed] transition-all">
              Continue Shopping
            </Link>
            {isAuthenticated && (
              <div className="mt-12 border-t border-[#d2d2d7] pt-8">
                <p className="text-xs text-[#86868b] mb-4 uppercase tracking-wider font-semibold">My Profile</p>
                <div className="flex flex-col items-center gap-3">
                  <a href="https://store.fozzil.in/shop/orders" className="text-sm text-[#1d1d1f] font-medium hover:text-[#065DC0] transition-colors">Orders</a>
                  <a href="https://store.fozzil.in/shop/saves" className="text-sm text-[#1d1d1f] font-medium hover:text-[#065DC0] transition-colors">Your Saves</a>
                  <a href="https://account.fozzil.in" target="_blank" rel="noopener noreferrer" className="text-sm text-[#1d1d1f] font-medium hover:text-[#065DC0] transition-colors">Account</a>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="store-page bg-white dark:bg-[#f5f5f7] min-h-screen transition-colors duration-400">
      {/* Promo Banner */}
      <div className="bg-[#f5f5f7] dark:bg-[#e8e8ed] text-center py-3 px-6 border-b border-[#d2d2d7]">
        <p className="text-sm text-[#1d1d1f] font-medium">
          Free setup and onboarding with every ARK purchase. <a href="https://support.fozzil.in/contact" target="_blank" rel="noopener noreferrer" className="text-[#065DC0] hover:underline">Learn more ⓘ</a>
        </p>
      </div>

      <div className="max-w-[980px] mx-auto px-6 py-10">
        {/* Hero: Bag total */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-10">
          <h1 className="text-[32px] md:text-[40px] font-semibold text-[#1d1d1f] leading-tight mb-4">
            Your bag total is {formatPrice(total)}.
          </h1>
          <button
            onClick={() => isAuthenticated ? navigate('/shop/checkout') : navigate('/auth')}
            className="px-12 py-3.5 rounded-full bg-[#065DC0] text-white text-[15px] font-semibold border-none cursor-pointer hover:bg-[#0077ed] transition-all inline-flex items-center gap-2"
          >
            Check Out
          </button>
        </motion.div>

        {/* Bag Items */}
        <div className="border-t border-[#d2d2d7]">
          {items.map((item, idx) => (
            <motion.div
              key={item.bagId}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="py-8 border-b border-[#d2d2d7]"
            >
              <div className="flex gap-6 md:gap-10">
                {/* Product Image */}
                <div className="w-[100px] md:w-[140px] shrink-0">
                  <img src={item.image} alt={item.name} className="w-full h-auto object-contain" />
                </div>

                {/* Product Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-[17px] md:text-[21px] font-semibold text-[#1d1d1f] mb-1">{item.name}</h3>
                      {item.config && item.config.map(({ label, value }) => (
                        <p key={label} className="text-sm text-[#6e6e73]">{value}</p>
                      ))}
                    </div>

                    {/* Quantity & Price */}
                    <div className="flex items-start gap-8 md:gap-12">
                      {/* Quantity Selector */}
                      <div className="relative">
                        <select
                          value={item.quantity}
                          onChange={(e) => updateQuantity(item.bagId, parseInt(e.target.value))}
                          className="appearance-none bg-transparent border border-[#d2d2d7] rounded-lg px-4 py-2 pr-8 text-sm font-medium text-[#1d1d1f] cursor-pointer outline-none focus:border-[#065DC0] transition-colors"
                        >
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                        <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6e6e73] pointer-events-none" />
                      </div>

                      {/* Price */}
                      <div className="text-right">
                        <p className="text-[17px] font-semibold text-[#1d1d1f]">{formatPrice(item.price * item.quantity)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Remove */}
                  <div className="mt-3 flex items-center gap-4">
                    <button
                      onClick={() => removeItem(item.bagId)}
                      className="text-[#065DC0] text-sm font-medium hover:underline cursor-pointer bg-transparent border-none p-0"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>

              {/* FOZZIL Care+ Add-on */}
              <div className="mt-6 sm:ml-[100px] md:ml-[140px] pl-0 sm:pl-6 md:pl-10">
                <div className="flex flex-col sm:flex-row items-start gap-3 bg-[#f5f5f7] dark:bg-black/10 p-4 rounded-2xl">
                  <span className="text-lg mt-0.5">🛡️</span>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#1d1d1f]">
                      Add FOZZIL Care+ for {item.name} for {formatPrice(Math.round(item.price * 0.15))}
                    </p>
                    <ul className="mt-2 text-sm text-[#6e6e73] space-y-1 list-disc list-inside">
                      <li>Extended hardware warranty and coverage</li>
                      <li>Priority specialist support 24/7</li>
                    </ul>
                    <a href="https://support.fozzil.in/contact" target="_blank" rel="noopener noreferrer" className="text-[#065DC0] text-sm mt-2 inline-flex items-center gap-1 hover:underline">
                      Learn more ⓘ
                    </a>
                  </div>
                  <button className="text-[#065DC0] text-sm font-medium ml-0 sm:ml-auto shrink-0 bg-transparent border border-[#065DC0] px-4 py-1.5 rounded-full cursor-pointer hover:bg-[#065DC0] hover:text-white transition-all">
                    Add
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Shipping Info */}
        <div className="py-8 border-b border-[#d2d2d7]">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-[15px] font-semibold text-[#1d1d1f] mb-1">
                Find out how soon you can get this item.{' '}
                <button onClick={() => setShowPinInput(!showPinInput)} className="text-[#065DC0] bg-transparent border-none cursor-pointer font-medium hover:underline p-0">
                  Enter pin code <ChevronDown size={12} className="inline" />
                </button>
              </p>
              {showPinInput && (
                <div className="mt-3 flex gap-2">
                  <input
                    type="text"
                    value={pinCode}
                    onChange={(e) => setPinCode(e.target.value)}
                    placeholder="Enter PIN code"
                    className="px-4 py-2 border border-[#d2d2d7] rounded-lg text-sm outline-none focus:border-[#065DC0] w-[180px]"
                  />
                  <button className="px-4 py-2 bg-[#065DC0] text-white text-sm font-medium rounded-lg border-none cursor-pointer hover:bg-[#0077ed]">Apply</button>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-6 mt-4">
            <div className="flex items-center gap-2 text-sm text-[#6e6e73]">
              <Truck size={18} className="text-[#6e6e73]" />
              <span>Ships in 5–7 business days</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#6e6e73]">
              <MapPin size={18} className="text-[#6e6e73]" />
              <span>Free setup at your location</span>
            </div>
          </div>
        </div>

        {/* Bag Summary */}
        <div className="py-8">
          <div className="max-w-[420px] ml-auto">
            <div className="flex justify-between py-2 text-[15px]">
              <span className="text-[#6e6e73]">Bag Subtotal</span>
              <span className="text-[#1d1d1f] font-medium">{formatPrice(subtotal)}</span>
            </div>
            <div className="flex justify-between py-2 text-[15px]">
              <span className="text-[#6e6e73]">Shipping</span>
              <span className="text-[#1d1d1f] font-medium uppercase">FREE</span>
            </div>
            <div className="border-t border-[#d2d2d7] mt-3 pt-4">
              <div className="flex justify-between items-baseline">
                <span className="text-[21px] font-bold text-[#1d1d1f]">Total</span>
                <span className="text-[24px] font-bold text-[#1d1d1f]">{formatPrice(total)}</span>
              </div>
            </div>
          </div>

          {/* Checkout Button */}
          <div className="max-w-[420px] ml-auto mt-6">
            <button
              onClick={() => isAuthenticated ? navigate('/shop/checkout') : navigate('/auth')}
              className="w-full sm:w-fit px-8 py-3.5 mx-auto rounded-full bg-[#065DC0] text-white text-base font-semibold border-none cursor-pointer hover:bg-[#0077ed] transition-all"
            >
              Check Out
            </button>
            {!isAuthenticated && (
              <p className="text-xs text-center text-[#86868b] mt-3">Sign in with FOZZIL ID to check out</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BagPage;
